import React, { useState } from 'react';
import { useAuth } from '../../../contexts/AuthContextLazy';
import { StatCard } from '../../../components/dashboard/widgets/StatCard';
import { ActivityFeed, ActivityItem } from '../../../components/dashboard/widgets/ActivityFeed';
import { LineChart } from '../../../components/dashboard/ChartComponents/LineChart';
import { DonutChart } from '../../../components/dashboard/ChartComponents/DonutChart';
import {
  UserGroupIcon,
  ServerIcon,
  LinkIcon,
  ChartBarIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  CogIcon
} from '@heroicons/react/24/outline';

export const AdminDashboard: React.FC = () => {
  const { userProfile } = useAuth();
  const [selectedPeriod, setSelectedPeriod] = useState('7d');

  // Mock system metrics
  const userGrowthData = [
    { label: 'Mon', value: 95 },
    { label: 'Tue', value: 98 },
    { label: 'Wed', value: 102 },
    { label: 'Thu', value: 105 },
    { label: 'Fri', value: 108 },
    { label: 'Sat', value: 107 },
    { label: 'Sun', value: 112 }
  ];

  const userDistribution = [
    { label: 'Clients', value: 85, color: '#003DA5' },
    { label: 'PCM Staff', value: 12, color: '#2A78BD' },
    { label: 'Client Admins', value: 8, color: '#10B981' },
    { label: 'Super Admins', value: 2, color: '#7C3AED' }
  ];

  type SystemStatus =
    | { status: string; latency: string; usage?: never; message?: never }
    | { status: string; latency?: never; usage: string; message?: never }
    | { status: string; latency?: never; usage?: never; message: string };

  const systemStatus: Record<string, SystemStatus> = {
    api: { status: 'operational', latency: '45ms' },
    database: { status: 'operational', latency: '12ms' },
    mymeetings: { status: 'warning', message: 'Rate limit 80%' },
    storage: { status: 'operational', usage: '45%' }
  };

  const recentActivity: ActivityItem[] = [
    {
      id: '1',
      type: 'alert',
      title: 'MyMeetings sync rate limit warning',
      description: 'Approaching 80% of hourly limit',
      timestamp: new Date(Date.now() - 0.5 * 60 * 60 * 1000),
      status: 'warning'
    },
    {
      id: '2',
      type: 'client',
      title: 'New organization created',
      description: 'Tech Innovations Corp',
      timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000),
      user: 'John Smith',
      status: 'success'
    },
    {
      id: '3',
      type: 'task',
      title: 'Database backup completed',
      description: 'Daily automated backup successful',
      timestamp: new Date(Date.now() - 6 * 60 * 60 * 1000),
      status: 'success'
    },
    {
      id: '4',
      type: 'alert',
      title: 'Feature flag updated',
      description: 'realtime_portfolio enabled for Premium tier',
      timestamp: new Date(Date.now() - 12 * 60 * 60 * 1000),
      user: 'System',
      status: 'info'
    },
    {
      id: '5',
      type: 'client',
      title: 'Staff member added',
      description: 'Sarah Johnson joined as PCM Staff',
      timestamp: new Date(Date.now() - 24 * 60 * 60 * 1000),
      user: 'Admin'
    }
  ];

  const teamActivity = [
    { name: 'John Smith', role: 'PCM Staff', lastActive: '2 min ago', status: 'online' },
    { name: 'Sarah Johnson', role: 'PCM Staff', lastActive: '15 min ago', status: 'online' },
    { name: 'Mike Chen', role: 'PCM Staff', lastActive: '1 hour ago', status: 'away' },
    { name: 'Lisa Park', role: 'Admin', lastActive: '3 hours ago', status: 'offline' }
  ];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Welcome Message */}
      <div className="mb-8">
        <p className="text-lg text-gray-600">
          Welcome back, {userProfile?.full_name}
        </p>
      </div>

      {/* System Metrics */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <StatCard
          title="Total Users"
          value="112"
          change={{ value: '+5.3%', isPositive: true }}
          icon={UserGroupIcon}
          iconBgColor="bg-blue-500"
        />
        <StatCard
          title="Active Organizations"
          value="68"
          change={{ value: '+3 this month', isPositive: true }}
          icon={ChartBarIcon}
          iconBgColor="bg-green-500"
        />
        <StatCard
          title="System Uptime"
          value="99.9%"
          icon={ServerIcon}
          iconBgColor="bg-purple-500"
        />
        <StatCard
          title="Active Integrations"
          value="3"
          icon={LinkIcon}
          iconBgColor="bg-amber-500"
        />
      </div>

      {/* System Health Status */}
      <div className="bg-white portal-content-shadow rounded-lg shadow-sm border border-gray-200 p-6 mb-8">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">System Health</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {Object.entries(systemStatus).map(([service, status]) => (
            <div key={service} className="border rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-700 capitalize">
                  {service === 'mymeetings' ? 'MyMeetings' : service}
                </span>
                {status.status === 'operational' ? (
                  <CheckCircleIcon className="h-5 w-5 text-green-500" />
                ) : status.status === 'warning' ? (
                  <ExclamationTriangleIcon className="h-5 w-5 text-amber-500" />
                ) : (
                  <ExclamationTriangleIcon className="h-5 w-5 text-red-500" />
                )}
              </div>
              <p className={`text-xs font-medium ${status.status === 'operational' ? 'text-green-600' :
                status.status === 'warning' ? 'text-amber-600' : 'text-red-600'
                }`}>
                {status.status.charAt(0).toUpperCase() + status.status.slice(1)}
              </p>
              {status.latency && (
                <p className="text-xs text-gray-500 mt-1">Latency: {status.latency}</p>
              )}
              {status.usage && (
                <p className="text-xs text-gray-500 mt-1">Usage: {status.usage}</p>
              )}
              {status.message && (
                <p className="text-xs text-amber-600 mt-1">{status.message}</p>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Left Column - 2/3 width */}
        <div className="lg:col-span-2 space-y-8">
          {/* User Growth Chart */}
          <div className="bg-white portal-content-shadow rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-semibold text-gray-900">User Growth</h2>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setSelectedPeriod('24h')}
                  className={`px-3 py-1 text-sm font-medium rounded-md ${selectedPeriod === '24h' ? 'text-white bg-blue-600' : 'text-gray-700 hover:bg-gray-100'
                    }`}
                >
                  24h
                </button>
                <button
                  onClick={() => setSelectedPeriod('7d')}
                  className={`px-3 py-1 text-sm font-medium rounded-md ${selectedPeriod === '7d' ? 'text-white bg-blue-600' : 'text-gray-700 hover:bg-gray-100'
                    }`}
                >
                  7d
                </button>
                <button
                  onClick={() => setSelectedPeriod('30d')}
                  className={`px-3 py-1 text-sm font-medium rounded-md ${selectedPeriod === '30d' ? 'text-white bg-blue-600' : 'text-gray-700 hover:bg-gray-100'
                    }`}
                >
                  30d
                </button>
              </div>
            </div>
            <LineChart data={userGrowthData} height={250} />
          </div>

          {/* Recent System Activity */}
          <ActivityFeed
            activities={recentActivity}
            maxItems={10}
            showViewAll={true}
            onViewAll={() => {
              if (import.meta.env.DEV) {
                console.log('View all system logs');
              }
            }}
          />

          {/* Integration Status */}
          <div className="bg-white portal-content-shadow rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Integration Status</h2>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 border rounded-lg">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <LinkIcon className="h-5 w-5 text-green-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">MyMeetings</p>
                    <p className="text-xs text-gray-500">Last sync: 5 min ago</p>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="text-xs font-medium text-amber-600 bg-amber-100 px-2 py-1 rounded-full">
                    Rate Limited
                  </span>
                  <button className="text-sm font-medium text-blue-600 hover:text-blue-700">
                    Configure →
                  </button>
                </div>
              </div>

              <div className="flex items-center justify-between p-4 border rounded-lg">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-gray-100 rounded-lg">
                    <CogIcon className="h-5 w-5 text-gray-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">Email Service</p>
                    <p className="text-xs text-gray-500">Not configured</p>
                  </div>
                </div>
                <button className="text-sm font-medium text-blue-600 hover:text-blue-700">
                  Setup →
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Right Column - 1/3 width */}
        <div className="space-y-8">
          {/* User Distribution */}
          <div className="bg-white portal-content-shadow rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">User Distribution</h3>
            <DonutChart
              data={userDistribution}
              centerLabel="Total Users"
              centerValue="107"
              size={200}
            />
          </div>

          {/* Team Activity */}
          <div className="bg-white portal-content-shadow rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Team Activity</h3>
            <div className="space-y-3">
              {teamActivity.map((member, index) => (
                <div key={index} className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className={`w-2 h-2 rounded-full ${member.status === 'online' ? 'bg-green-500' :
                      member.status === 'away' ? 'bg-amber-500' : 'bg-gray-300'
                      }`} />
                    <div>
                      <p className="text-sm font-medium text-gray-900">{member.name}</p>
                      <p className="text-xs text-gray-500">{member.role}</p>
                    </div>
                  </div>
                  <span className="text-xs text-gray-500">{member.lastActive}</span>
                </div>
              ))}
            </div>
            <button className="mt-4 text-sm font-medium text-blue-600 hover:text-blue-700">
              View all team members →
            </button>
          </div>

          {/* Quick Admin Actions */}
          <div className="bg-white portal-content-shadow rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Admin Actions</h3>
            <div className="space-y-2">
              <button className="w-full text-left px-4 py-3 bg-gray-50 hover:bg-gray-100 rounded-lg flex items-center justify-between group">
                <span className="text-sm font-medium text-gray-700">Manage Features</span>
                <span className="text-gray-400 group-hover:text-gray-600">→</span>
              </button>
              <button className="w-full text-left px-4 py-3 bg-gray-50 hover:bg-gray-100 rounded-lg flex items-center justify-between group">
                <span className="text-sm font-medium text-gray-700">View Logs</span>
                <span className="text-gray-400 group-hover:text-gray-600">→</span>
              </button>
              <button className="w-full text-left px-4 py-3 bg-gray-50 hover:bg-gray-100 rounded-lg flex items-center justify-between group">
                <span className="text-sm font-medium text-gray-700">System Settings</span>
                <span className="text-gray-400 group-hover:text-gray-600">→</span>
              </button>
              <button className="w-full text-left px-4 py-3 bg-gray-50 hover:bg-gray-100 rounded-lg flex items-center justify-between group">
                <span className="text-sm font-medium text-gray-700">Database Health</span>
                <span className="text-gray-400 group-hover:text-gray-600">→</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};